// src/components/ActivityTypeLadder.jsx
// Ported from takso-poc/takso-reporting-ui/src/components/ActivityTypeLadder.jsx.
//
// Ladder bar chart for activity types (sEventDetail), styled to match the
// outcome ladder. Bars are coloured by their output group (Infrastructure /
// Developmental / Presentational) using the official Takso palette colours.
//
// Default view — RANKED: all used types sorted by count desc, then all unused
//   types alphabetically at the bottom, faded. No group headers.
//
// Grouped view — GROUPED: three sub-ladders, ordered by group total count desc.
//   Within each sub-ladder: used by count desc, then unused alphabetically.

import { useTranslation } from 'react-i18next';

import { LocaleService } from '../../services';

import '../../charts/charts.scss';

// Non-top bars scale to this %. Top bar uses flex:1.
const MAX_BAR_PCT = 93;

// Official Takso output-type palette.
const GROUP_COLOR = {
  Infrastructure: '#264DF7',
  Developmental: '#1E96FC',
  Presentational: '#A2D6F9',
  'Output TBC': '#9ca3af',
};

const fmtN = (n) => (n == null ? '' : LocaleService.fmtLocaleNumeral(n));
const fmtPct = (n, d) => (d > 0 ? `${Math.round((n / d) * 100)}%` : '0%');

/**
 * Rule-based 2–3 sentence plain-language description of the activity type data.
 * No LLM — mirrors the style of buildProseSummary / buildParticipantProse.
 */
function buildActivityTypeProse(t, data) {
  const groups = data?.groups || [];
  if (groups.length === 0) return null;

  const usedGroups = groups
    .filter((g) => g.count > 0)
    .sort((a, b) => b.count - a.count);

  if (usedGroups.length === 0) return null;

  const total = usedGroups.reduce((s, g) => s + g.count, 0);

  // Sentence 1 — top group dominance
  const top = usedGroups[0];
  const second = usedGroups[1];
  let s1;
  if (usedGroups.length === 1) {
    s1 = t('charts.activity_type_prose.breakdown1', {
      activities: t('charts.num_activities', {
        count: total,
        countFmt: fmtN(total),
      }),
      classifiedAs: top.group,
    });
  } else {
    s1 = t('charts.activity_type_prose.breakdown2', {
      activities: t('charts.num_activities', {
        count: total,
        countFmt: fmtN(total),
      }),
      outputGroups: t('charts.num_output_groups', { count: usedGroups.length }),
      largestGroup: top.group,
      largestGroupNumActivities: t('charts.num_activities', {
        count: top.count,
        countFmt: fmtN(top.count),
      }),
      largestGroupPct: fmtPct(top.count, total),
      secondLargestGroup: second.group,
      secondLargestGroupNumActivities: t('charts.num_activities', {
        count: second.count,
        countFmt: fmtN(second.count),
      }),
      secondLargestGroupPct: fmtPct(second.count, total),
    });
  }

  // Sentence 2 — top individual type across all groups
  const allTypes = groups.flatMap((g) =>
    g.types.map((t) => ({ ...t, group: g.group }))
  );
  const topType = allTypes
    .filter((t) => t.count > 0)
    .sort((a, b) => b.count - a.count)[0];

  const s2 = topType
    ? t('charts.activity_type_prose.most_common_acitity_type', {
        mostCommonType: topType.name,
        activities: t('charts.num_activities', {
          count: topType.count,
          countFmt: fmtN(topType.count),
        }),
        pct: fmtPct(topType.count, total),
      })
    : null;

  return [s1, s2].filter(Boolean).join(' ');
}

// ─── Single type row ─────────────────────────────────────────────────────────
function TypeRow({ type, color, maxCount }) {
  const isUsed = type.count > 0;
  const pct = isUsed ? (type.count / maxCount) * MAX_BAR_PCT : 0;
  const isTopBar = isUsed && type.count === maxCount;

  return (
    <div className="chart-bar-row">
      <div
        className={`chart-bar-row__label${
          isUsed ? '' : ' chart-bar-row__label--unused'
        }`}
      >
        {type.name}
      </div>

      <div className="chart-bar-row__bar-area">
        {isUsed ? (
          <>
            <div
              className={`chart-bar-row__bar${
                isTopBar ? ' chart-bar-row__bar--top' : ''
              }`}
              style={{
                background: color,
                ...(!isTopBar && { width: `${pct}%` }),
              }}
            />
            <span className="chart-bar-row__count">{fmtN(type.count)}</span>
          </>
        ) : (
          <div className="chart-bar-row__bar-spacer" />
        )}
      </div>
    </div>
  );
}

// ─── Group header (grouped view only) ────────────────────────────────────────
function GroupHeader({ group, count }) {
  return (
    <div className="chart-group__header">
      <span className="chart-group__name">{group}</span>
      <span className="chart-group__count">{fmtN(count)}</span>
    </div>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────
export function ActivityTypeLadder({
  data,
  hideUnused = false,
  view = 'ranked',
}) {
  const { t } = useTranslation();
  const groups = data?.groups || [];
  const allTypes = groups.flatMap((g) =>
    g.types.map((t) => ({ ...t, group: g.group, groupCount: g.count }))
  );

  const maxCount = Math.max(...allTypes.map((t) => t.count), 1);

  if (allTypes.length === 0) {
    return (
      <div className="chart-empty chart-empty--centered">
        {t('charts.no_activity_type_data_available')}
      </div>
    );
  }

  const prose = buildActivityTypeProse(t, data);

  return (
    <div>
      <div className="chart-bar-list">
        {view === 'ranked'
          ? (() => {
              const used = allTypes
                .filter((t) => t.count > 0)
                .sort(
                  (a, b) => b.count - a.count || a.name.localeCompare(b.name)
                );
              const unused = hideUnused
                ? []
                : allTypes
                    .filter((t) => t.count === 0)
                    .sort((a, b) => a.name.localeCompare(b.name));
              return [...used, ...unused].map((t) => (
                <TypeRow
                  key={t.typeId}
                  type={t}
                  color={GROUP_COLOR[t.group] || '#9ca3af'}
                  maxCount={maxCount}
                />
              ));
            })()
          : [...groups]
              .sort(
                (a, b) => b.count - a.count || a.group.localeCompare(b.group)
              )
              .filter((group) => !hideUnused || group.count > 0)
              .map((group) => {
                const color = GROUP_COLOR[group.group] || '#9ca3af';
                const used = group.types
                  .filter((t) => t.count > 0)
                  .sort(
                    (a, b) => b.count - a.count || a.name.localeCompare(b.name)
                  );
                const unused = hideUnused
                  ? []
                  : group.types
                      .filter((t) => t.count === 0)
                      .sort((a, b) => a.name.localeCompare(b.name));
                return (
                  <div key={group.group} className="chart-group">
                    <GroupHeader group={group.group} count={group.count} />
                    {[...used, ...unused].map((t) => (
                      <TypeRow
                        key={t.typeId}
                        type={t}
                        color={color}
                        maxCount={maxCount}
                      />
                    ))}
                  </div>
                );
              })}
      </div>
      {prose && <p className="chart-prose">{prose}</p>}
    </div>
  );
}
